import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Todos os cards de um quadro (fluxo) ficam com a altura do maior card visível
// nele. Cada card informa a altura natural do seu conteúdo; o provider guarda
// a maior e devolve como altura mínima para todos.
const KanbanCardHeightContext = createContext(null);

export const KanbanCardHeightProvider = ({ children }) => {
  const heightsRef = useRef(new Map());
  const pendingRef = useRef(false);
  const mountedRef = useRef(true);
  const [cardHeight, setCardHeight] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const report = useCallback((key, height) => {
    const heights = heightsRef.current;
    if (height == null) {
      if (!heights.delete(key)) return;
    } else {
      if (heights.get(key) === height) return;
      heights.set(key, height);
    }
    if (pendingRef.current) return;
    pendingRef.current = true;
    // Microtask: junta os relatos de todos os cards do mesmo commit e aplica
    // antes da pintura, sem piscar com alturas diferentes.
    Promise.resolve().then(() => {
      pendingRef.current = false;
      if (!mountedRef.current) return;
      let max = 0;
      heightsRef.current.forEach((h) => {
        if (h > max) max = h;
      });
      setCardHeight(max);
    });
  }, []);

  const value = useMemo(() => ({ cardHeight, report }), [cardHeight, report]);

  return (
    <KanbanCardHeightContext.Provider value={value}>
      {children}
    </KanbanCardHeightContext.Provider>
  );
};

// Mede a altura natural do card: paddings do conteúdo + filhos dele (que não
// esticam) + linha de ícones. Não dá para ler a altura do próprio card: com a
// altura mínima aplicada ele nunca encolheria de volta quando o maior card
// sai do quadro. Devolve a altura mínima comum (0 fora de um quadro).
export const useKanbanCardHeight = ({ contentRef, actionsRef }, remeasureKey) => {
  const ctx = useContext(KanbanCardHeightContext);
  const report = ctx?.report;
  const keyRef = useRef({});

  useLayoutEffect(() => {
    const content = contentRef.current;
    const actions = actionsRef.current;
    if (!report || !content || !actions) return undefined;
    const key = keyRef.current;

    const measure = () => {
      const style = window.getComputedStyle(content);
      let height =
        parseFloat(style.paddingTop) +
        parseFloat(style.paddingBottom) +
        actions.getBoundingClientRect().height;
      Array.from(content.children).forEach((child) => {
        height += child.getBoundingClientRect().height;
      });
      report(key, Math.ceil(height));
    };

    measure();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(actions);
      Array.from(content.children).forEach((child) => observer.observe(child));
    }

    return () => {
      if (observer) observer.disconnect();
      report(key, null);
    };
  }, [report, remeasureKey, contentRef, actionsRef]);

  return ctx?.cardHeight || 0;
};
